'use client';
import React, { useState, useEffect } from 'react';

const STORAGE_KEY = 'xau_risk_disclaimer_accepted';

export default function RiskDisclaimerModal() {
  const [visible, setVisible] = useState(false);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    const accepted = localStorage.getItem(STORAGE_KEY);
    if (!accepted) {
      setVisible(true);
    }
  }, []);

  const handleAccept = () => {
    if (!checked) return;
    localStorage.setItem(STORAGE_KEY, 'true');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="relative w-full max-w-lg bg-[#0f0f17] border border-[#d4af37]/30 rounded-2xl shadow-2xl shadow-black/60 overflow-hidden">
        {/* Gold top accent bar */}
        <div className="h-1 w-full bg-gradient-to-r from-[#d4af37]/0 via-[#d4af37] to-[#d4af37]/0" />

        <div className="p-6 sm:p-8">
          {/* Header */}
          <div className="flex items-center gap-3 mb-5">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
              <svg className="w-5 h-5 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white font-mono tracking-wide">Risk Disclaimer</h2>
              <p className="text-xs text-zinc-500 font-mono">Please read before continuing</p>
            </div>
          </div>

          {/* Disclaimer body */}
          <div className="bg-[#09090f] border border-white/5 rounded-xl p-4 mb-5 max-h-52 overflow-y-auto text-sm text-zinc-400 leading-relaxed space-y-3 font-mono scrollbar-thin scrollbar-thumb-zinc-700 scrollbar-track-transparent">
            <p>
              <span className="text-amber-400 font-semibold">XAU Engine</span> provides AI-generated trading signals for educational and informational purposes only. These signals do <span className="text-white">not</span> constitute financial advice, investment recommendations, or solicitation to buy or sell any financial instrument.
            </p>
            <p>
              Trading gold (XAU/USD) and other financial instruments involves substantial risk of loss and is not suitable for all investors. Past performance of signals is not indicative of future results.
            </p>
            <p>
              You may lose some or all of your invested capital. Never trade with money you cannot afford to lose. You are solely responsible for your own trading decisions.
            </p>
            <p>
              By using this platform, you acknowledge that XAU Engine and its operators are not liable for any financial losses incurred as a result of using the information provided.
            </p>
          </div>

          {/* Checkbox */}
          <label className="flex items-start gap-3 cursor-pointer group mb-6 select-none">
            <div
              onClick={() => setChecked(prev => !prev)}
              className={`flex-shrink-0 mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200 ${
                checked
                  ? 'bg-[#d4af37] border-[#d4af37]'
                  : 'bg-transparent border-zinc-600 group-hover:border-[#d4af37]/60'
              }`}
            >
              {checked && (
                <svg className="w-3 h-3 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
            <span
              onClick={() => setChecked(prev => !prev)}
              className="text-sm text-zinc-300 leading-snug font-mono"
            >
              I understand that trading involves significant risk of loss, and I am using XAU Engine signals for informational purposes only. I accept full responsibility for my trading decisions.
            </span>
          </label>

          {/* CTA */}
          <button
            onClick={handleAccept}
            disabled={!checked}
            className={`w-full py-3 rounded-xl font-semibold font-mono text-sm tracking-widest uppercase transition-all duration-200 ${
              checked
                ? 'bg-[#d4af37] text-black hover:bg-[#e5c84a] cursor-pointer shadow-lg shadow-[#d4af37]/20'
                : 'bg-zinc-800 text-zinc-600 cursor-not-allowed'
            }`}
          >
            I Understand — Enter Dashboard
          </button>
        </div>
      </div>
    </div>
  );
}
